import { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Square, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { supabase } from '@/lib/supabase';

interface Project {
  id: string;
  title: string;
}

interface TimerWidgetProps {
  projects: Project[];
  onTimerStopped: () => void;
}

export function TimerWidget({ projects, onTimerStopped }: TimerWidgetProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return clearTimer;
  }, [clearTimer]);

  const formatElapsed = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleStart = async () => {
    try {
      setIsProcessing(true);
      await supabase.functions.invoke('time-entry', {
        body: {
          action: 'start',
          task_id: selectedTask || undefined,
          project_id: selectedProject || undefined,
        },
      });

      startTimeRef.current = Date.now();
      setElapsed(0);
      setIsRunning(true);

      intervalRef.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch {
      // Silently handle error; user can retry
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStop = async () => {
    try {
      setIsProcessing(true);
      clearTimer();

      await supabase.functions.invoke('time-entry', {
        body: { action: 'stop' },
      });

      setIsRunning(false);
      setElapsed(0);
      onTimerStopped();
    } catch {
      // Silently handle error; user can retry
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 pt-4">
        <Clock className="h-5 w-5 text-primary" />

        {/* Elapsed time display */}
        <div className="min-w-[100px] text-center">
          <span className={`font-mono text-2xl font-bold tabular-nums ${isRunning ? 'text-primary' : ''}`}>
            {formatElapsed(elapsed)}
          </span>
        </div>

        {/* Task selector */}
        <Select value={selectedTask} onValueChange={setSelectedTask} disabled={isRunning}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select task (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No task</SelectItem>
          </SelectContent>
        </Select>

        {/* Project selector */}
        <Select value={selectedProject} onValueChange={setSelectedProject} disabled={isRunning}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Select project (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No project</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Start / Stop button */}
        {!isRunning ? (
          <Button
            onClick={handleStart}
            disabled={isProcessing}
            className="gap-2"
          >
            <Play className="h-4 w-4" />
            {isProcessing ? 'Starting...' : 'Start'}
          </Button>
        ) : (
          <Button
            onClick={handleStop}
            disabled={isProcessing}
            variant="destructive"
            className="gap-2"
          >
            <Square className="h-4 w-4" />
            {isProcessing ? 'Stopping...' : 'Stop'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
